/**
 * The exposure drawer rides its own history entry so Back dismisses it, and it builds that entry
 * from the URL the member is already standing on.
 *
 * Everything else in the query belongs to the page under the drawer — `?person=` is what keeps an
 * impersonated session pointed at the right member on reload, and a broker search keeps its terms
 * — so opening the drawer adds one key and closing it removes one key.
 */
const DRAWER_QUERY_KEYS = ['exposure', 'company'] as const;

type DrawerQueryKey = (typeof DRAWER_QUERY_KEYS)[number];

function currentUrl(): { pathname: string; params: URLSearchParams } {
    return {
        pathname: window.location.pathname,
        params: new URLSearchParams(window.location.search),
    };
}

function format(pathname: string, params: URLSearchParams): string {
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
}

/** The current URL with the drawer's subject set, every other query key preserved. */
export function urlWithDrawerQuery(key: DrawerQueryKey, value: string | number): string {
    const { pathname, params } = currentUrl();

    for (const drawerKey of DRAWER_QUERY_KEYS) {
        params.delete(drawerKey);
    }
    params.set(key, String(value));

    return format(pathname, params);
}

/** The current URL with the drawer's subject removed, every other query key preserved. */
export function urlWithoutDrawerQuery(): string {
    const { pathname, params } = currentUrl();

    for (const drawerKey of DRAWER_QUERY_KEYS) {
        params.delete(drawerKey);
    }

    return format(pathname, params);
}
